import React from "react";
import PropTypes from "prop-types";
import Sparkline from "../Sparkline";
import "./MetricTile.scss";

/**
 * MetricTile — one headline figure, with what it was last period and its trend.
 *
 * The change is coloured by whether it is GOOD, not by its sign: pass `invert` for a
 * metric where down is better (bounce, bots). A change off a tiny base is shown but
 * muted, because "+200%" from 1 to 3 is noise dressed as news.
 *
 * `footer` + `status` add a line at the foot saying where the figure comes from and how
 * fresh it is: `status="ok"` (green dot), `"stale"` (amber — a feed that should be
 * talking has gone quiet), `"quiet"` (grey — quiet by design, e.g. between mailshots).
 */
const MetricTile = ({
  label, value, previous, format, hint, spark, color, invert, onClick, active, emphasis, small, footer, status,
}) => {
  const f = format || ((n) => (n == null ? "—" : Number(n).toLocaleString("en-GB")));
  const change = value != null && previous != null && previous !== 0 ? (value - previous) / previous : null;
  const good = change == null ? null : invert ? change < 0 : change > 0;
  const tinyBase = previous != null && previous < 5;
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      className={[
        "metric-tile",
        onClick && "metric-tile--clickable",
        active && "metric-tile--active",
        emphasis && "metric-tile--emphasis",
        small && "metric-tile--small",
      ].filter(Boolean).join(" ")}
      onClick={onClick}
      style={color ? { "--metric-color": color } : undefined}
    >
      <span className="metric-tile__label">{label}</span>
      <span className="metric-tile__value">{f(value)}</span>
      {change != null && (
        <span
          className={`metric-tile__change metric-tile__change--${change === 0 ? "flat" : good ? "good" : "bad"}${tinyBase ? " metric-tile__change--muted" : ""}`}
          title={`Previous period: ${f(previous)}`}
        >
          {change > 0 ? "▲" : change < 0 ? "▼" : "■"} {Math.abs(change * 100) < 10 ? Math.abs(change * 100).toFixed(1) : Math.round(Math.abs(change * 100))}%
          <span className="metric-tile__was"> from {f(previous)}</span>
        </span>
      )}
      {hint && <span className="metric-tile__hint">{hint}</span>}
      {spark && spark.length > 1 && (
        <span className="metric-tile__spark">
          <Sparkline data={spark} width={160} height={28} color={color || "var(--tool-color, #0e7490)"} fill />
        </span>
      )}
      {footer && (
        <span className={`metric-tile__footer${status ? ` metric-tile__footer--${status}` : ""}`}>{footer}</span>
      )}
    </Tag>
  );
};

MetricTile.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.number,
  previous: PropTypes.number,
  format: PropTypes.func,
  hint: PropTypes.node,
  spark: PropTypes.arrayOf(PropTypes.number),
  color: PropTypes.string,
  invert: PropTypes.bool,
  onClick: PropTypes.func,
  active: PropTypes.bool,
  emphasis: PropTypes.bool,
  small: PropTypes.bool,
  footer: PropTypes.node,
  status: PropTypes.oneOf(["ok", "stale", "quiet"]),
};

export default MetricTile;
